import React, { useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Formik, Form as FormikForm, useField } from 'formik';

import { Breadcrumb, NavLinkButton, Button, Alert } from '@shared/components/bootstrap';
import { FormikFileFormGroup } from '@shared/components/fields/formik/formik_file_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { FormikHTMLFormGroup } from '@admin/components/fields/formik/formik_html_form_group';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Panel } from '@admin/components/helpers/panel';
import { LandingLocationReviewsPanel } from '@admin/components/landing/location/reviews/panel';
import { Spinner } from '@admin/components/spinner';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Landing__Location__ContentInput,
  LandingLocationContentQuery,
  useLandingLocationContentQuery,
  useLandingLocationContentUpsertMutation,
  Landing__Location__Service,
  Landing__Location__Content,
} from '@admin/schema';

type FormInput = Omit<Landing__Location__ContentInput, 'service' | 'locationID'> & {
  service?: Landing__Location__ContentInput['service'];
};

const Fields: React.FC<{
  content?: LandingLocationContentQuery['content'];
  locationID?: string;
  onSave(content: Pick<Landing__Location__Content, 'id'>): void;
}> = ({ content, locationID, onSave }) => {
  const [save, { loading, data }] = useLandingLocationContentUpsertMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.resource) onSave(result.resource);
    },
  });

  const onSubmit = ({ service, title, description, ...input }: FormInput) => {
    if (!service) return;

    save({
      variables: {
        id: content?.id,
        input: {
          ...input,
          locationID,
          service,
          title: title || null,
          description: description || null,
        },
      },
    });
  };

  return (
    <Formik<FormInput>
      initialValues={{
        service: content?.service,
        blurb: content?.trixHTML ?? '',
        title: content?.title ?? '',
        headline: content?.headline ?? '',
        description: content?.description ?? '',
        heroAlt: content?.heroAlt ?? '',
        reviews:
          content?.reviews?.map((review) => ({
            id: review.id,
            author: review.author,
            message: review.message,
            rating: review.rating,
          })) ?? [],
      }}
      onSubmit={onSubmit}
    >
      <FormikForm>
        {data?.result?.error && <Alert style="danger">{data.result.error}</Alert>}

        <Panel>
          <Panel.Header>
            <Panel.Title>Content</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            <FormikSelectFormGroup id="service" name="service" label="Service:">
              <option> - Service - </option>
              <option value={Landing__Location__Service.Moving}>Moving</option>
              <option value={Landing__Location__Service.SmartStorage}>Smart Storage</option>
            </FormikSelectFormGroup>
            <FormikInputFormGroup id="title" name="title" label="Title:" />
            <FormikInputFormGroup id="headline" name="headline" label="Headline:" />
            <FormikInputFormGroup id="description" name="description" label="Description:" />
            <FormikHTMLFormGroup id="blurb" name="blurb" label="Blurb:" />
            <FormikInputFormGroup id="heroAlt" name="heroAlt" label="Hero Alt:" />
            <FormikFileFormGroup id="heroImage" name="heroImage" label="Hero Image:" accept="image/*" />
          </Panel.Body>
        </Panel>

        <LandingLocationReviewsPanel />

        <Panel>
          <Panel.Footer align="right">
            {content?.id && (
              <NavLinkButton disabled={loading} kind="default" to={`/landing/location/contents/${content.id}`}>
                Cancel
              </NavLinkButton>
            )}
            <Button loading={loading} kind="primary" type="submit">
              Save
            </Button>
          </Panel.Footer>
        </Panel>
      </FormikForm>
    </Formik>
  );
};

export const LandingLocationContentForm: React.FC = () => {
  const [locationID] = useURLSearchParam('location_id');
  const { id } = useParams<{ id?: string }>();
  useTitle(id ? 'Landing - Location - Contents - Edit' : 'Landing - Location - Contents - New');
  const navigate = useNavigate();
  const { data } = useLandingLocationContentQuery({
    client,
    fetchPolicy: 'network-only',
    variables: { id: id! },
    skip: !id,
  });
  const content = data?.content;

  const onSave = (content: Pick<Landing__Location__Content, 'id'>) => {
    navigate(`/landing/location/contents/${content.id}`);
  };

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>Landing</Breadcrumb.Item>
          <Breadcrumb.Item>Location</Breadcrumb.Item>
          <Breadcrumb.Item>Content</Breadcrumb.Item>
          {id && (
            <Breadcrumb.Item>
              <NavLink to={`/landing/location/contents/${id}`}>{id}</NavLink>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>{id ? 'Edit' : 'New'}</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      {!id || content ? <Fields content={content} locationID={locationID} onSave={onSave} /> : <Spinner />}
    </>
  );
};
